// src/services/whatsappService.ts
//
// Configuração e disparo de notificações via WhatsApp Business Platform
// (Meta Cloud API). O envio em si acontece na Edge Function
// "whatsapp-notificar" (chamada automaticamente por um trigger do banco
// quando uma OS entra em "Aguardando Assinatura", ou manualmente por aqui
// para testes/reenvios).

import { supabase, supabaseAnonKey } from '../lib/supabase';

export interface ConfiguracaoWhatsapp {
  id: number;
  ativo: boolean;
  phone_number_id: string | null;
  business_account_id: string | null;
  numero_exibicao: string | null;
  webhook_verify_token: string | null;
  url_publica_app: string | null;
  possui_access_token: boolean;
  possui_app_secret: boolean;
  updated_at: string;
}

export interface NotificacaoTemplate {
  evento_codigo: string;
  descricao: string;
  template_name: string | null;
  idioma: string;
  ativo: boolean;
  variaveis_ajuda: string | null;
  updated_at: string;
}

export interface NotificacaoLog {
  id: number;
  evento_codigo: string;
  pessoa_id: number | null;
  os_id: number | null;
  telefone: string;
  template_name: string | null;
  status: string;
  erro_detalhe: string | null;
  meta_message_id: string | null;
  created_at: string;
  updated_at: string;
}

async function invocarFuncao<T = any>(body: Record<string, unknown>): Promise<T> {
  // Mesmo motivo documentado em src/lib/fiscalApi.ts: sem sessão do Supabase
  // Auth (este app usa Firebase Auth), o Authorization precisa ser explícito
  // ou o próprio gateway barra a chamada com 401 antes da função rodar.
  const { data, error } = await supabase.functions.invoke('whatsapp-notificar', {
    body,
    headers: { Authorization: `Bearer ${supabaseAnonKey}` },
  });
  if (error) {
    throw new Error(`Falha ao chamar whatsapp-notificar: ${error.message}`);
  }
  return data as T;
}

export const whatsappService = {
  async getConfiguracao(): Promise<ConfiguracaoWhatsapp> {
    const { data, error } = await supabase
      .from('configuracao_whatsapp')
      .select('id, ativo, phone_number_id, business_account_id, numero_exibicao, webhook_verify_token, url_publica_app, access_token_vault_id, app_secret_vault_id, updated_at')
      .eq('id', 1)
      .maybeSingle();

    if (error) throw new Error(error.message);

    return {
      id: data?.id ?? 1,
      ativo: data?.ativo ?? false,
      phone_number_id: data?.phone_number_id ?? null,
      business_account_id: data?.business_account_id ?? null,
      numero_exibicao: data?.numero_exibicao ?? null,
      webhook_verify_token: data?.webhook_verify_token ?? null,
      url_publica_app: data?.url_publica_app ?? null,
      possui_access_token: !!data?.access_token_vault_id,
      possui_app_secret: !!data?.app_secret_vault_id,
      updated_at: data?.updated_at ?? new Date().toISOString(),
    };
  },

  /**
   * Salva os campos não sensíveis diretamente. O Access Token e o App Secret
   * (sensíveis) são salvos via Vault por uma função RPC dedicada, nunca
   * gravados em texto puro numa coluna normal.
   */
  async salvarConfiguracao(params: {
    ativo: boolean;
    phone_number_id: string;
    business_account_id: string;
    numero_exibicao: string;
    webhook_verify_token: string;
    url_publica_app: string;
  }): Promise<void> {
    const { error } = await supabase
      .from('configuracao_whatsapp')
      .update({
        ativo: params.ativo,
        phone_number_id: params.phone_number_id || null,
        business_account_id: params.business_account_id || null,
        numero_exibicao: params.numero_exibicao || null,
        webhook_verify_token: params.webhook_verify_token || null,
        url_publica_app: params.url_publica_app || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', 1);

    if (error) throw new Error(error.message);
  },

  /**
   * Salva o Access Token (ou App Secret) no Supabase Vault e vincula o
   * secret_id na configuração, via função RPC (roda com privilégio elevado
   * no banco — o cliente nunca lê o valor de volta).
   */
  async salvarSegredo(tipo: 'access_token' | 'app_secret', valor: string): Promise<void> {
    const { error } = await supabase.rpc('salvar_segredo_whatsapp', {
      p_tipo: tipo,
      p_valor: valor,
    });
    if (error) throw new Error(error.message);
  },

  async getTemplates(): Promise<NotificacaoTemplate[]> {
    const { data, error } = await supabase
      .from('notificacao_whatsapp_template')
      .select('*')
      .order('evento_codigo', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as NotificacaoTemplate[]) || [];
  },

  async salvarTemplate(evento_codigo: string, params: { template_name: string; idioma: string; ativo: boolean }): Promise<void> {
    const { error } = await supabase
      .from('notificacao_whatsapp_template')
      .update({
        template_name: params.template_name || null,
        idioma: params.idioma || 'pt_BR',
        ativo: params.ativo,
        updated_at: new Date().toISOString(),
      })
      .eq('evento_codigo', evento_codigo);
    if (error) throw new Error(error.message);
  },

  async getLogs(limit = 50): Promise<NotificacaoLog[]> {
    const { data, error } = await supabase
      .from('notificacao_whatsapp_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data as NotificacaoLog[]) || [];
  },

  async testarConexao(): Promise<{ sucesso: boolean; mensagem: string; numero?: string; nome_verificado?: string; qualidade?: string }> {
    return invocarFuncao({ operacao: 'testar_conexao' });
  },

  async reenviarNotificacao(logId: number): Promise<{ sucesso: boolean; mensagem: string }> {
    return invocarFuncao({ operacao: 'reenviar', log_id: logId });
  },

  async notificarEvento(evento_codigo: string, os_id: number): Promise<{ sucesso: boolean; mensagem: string }> {
    return invocarFuncao({ operacao: 'notificar', evento_codigo, os_id });
  },
};
